import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { combineLatest } from 'rxjs';

import { notArchivedSelector } from '@flintmint/core-shared/rxdb';

import { useSyncStatus } from '@/providers/rxdb/sync-status';
import { useRxDb } from '@/providers/rxdb/rx-hooks';
import { documentCollections } from '@/providers/rxdb/constants';

import { getNextFakeProgress } from '@/utils/helper-utils';
import { initialSyncBannerProgressAtom, initialSyncBannerVisibleAtom, knownEmptyAccountAtom } from '@/state';

const PROGRESS_TICK_MS = 1000;
const COMPLETION_HOLD_MS = 300;

export const useInitialSyncBanner = () => {
  const { initialSyncComplete, initialSyncResolved } = useSyncStatus();
  const knownEmptyAccount = useAtomValue(knownEmptyAccountAtom);
  const hasLiveLocalDoc = useHasLiveLocalDoc();

  const [syncProgress, setSyncProgress] = useAtom(initialSyncBannerProgressAtom);
  const [showSyncBanner, setShowSyncBanner] = useAtom(initialSyncBannerVisibleAtom);

  // Display-only fast path: a known/assumed-empty account has nothing to sync, so the
  // banner treats it as sync-complete (never shows, or completes out if already shown).
  const syncSettled = initialSyncComplete || knownEmptyAccount;

  // Show only once live data actually exists locally while initial sync is still
  // incomplete, so an account with nothing to pull never flashes the banner.
  useEffect(() => {
    if (initialSyncResolved && !syncSettled && hasLiveLocalDoc && !showSyncBanner) {
      setSyncProgress(0);
      setShowSyncBanner(true);
    }
  }, [initialSyncResolved, syncSettled, hasLiveLocalDoc, showSyncBanner, setSyncProgress, setShowSyncBanner]);

  useEffect(() => {
    if (!showSyncBanner || syncSettled) {
      return;
    }

    const interval = setInterval(() => {
      setSyncProgress(getNextFakeProgress);
    }, PROGRESS_TICK_MS);

    return () => clearInterval(interval);
  }, [showSyncBanner, syncSettled, setSyncProgress]);

  useEffect(() => {
    if (!syncSettled || !showSyncBanner) {
      return;
    }

    setSyncProgress(100);

    // Progress stays at 100 through the collapse exit — Collapse keeps children
    // mounted while it animates, so zeroing here would flash an empty bar.
    const timeout = setTimeout(() => {
      setShowSyncBanner(false);
    }, COMPLETION_HOLD_MS);

    return () => clearTimeout(timeout);
  }, [syncSettled, showSyncBanner, setSyncProgress, setShowSyncBanner]);

  return {
    showSyncBanner,
    syncProgress,
  };
};

// Reactive "at least one live local document exists" across the document-surface
// collections (RxDB queries already exclude _deleted docs; archived docs are
// excluded explicitly — they replicate outside the initial-sync latch and don't
// count as syncing content here). False until the DB is ready — no data yet is
// indistinguishable from no DB for the banner's purposes.
const useHasLiveLocalDoc = (): boolean => {
  const db = useRxDb();
  const [hasLiveLocalDoc, setHasLiveLocalDoc] = useState(false);

  useEffect(() => {
    if (!db) {
      setHasLiveLocalDoc(false);
      return;
    }

    const subscription = combineLatest(
      documentCollections.map((collectionName) => db.collections[collectionName].findOne({ selector: notArchivedSelector }).$)
    ).subscribe((docs) => setHasLiveLocalDoc(docs.some((doc) => doc !== null)));

    return () => subscription.unsubscribe();
  }, [db]);

  return hasLiveLocalDoc;
};
